import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

const guideArticles = [
  { title: "Complete Buying Guide", url: "/guide/vigorbuy-spreadsheet-guide" },
  { title: "Best Hoodies & Sweaters", url: "/guide/best-hoodies-sweaters-vigorbuy-spreadsheet" },
  { title: "Best T-Shirts & Streetwear", url: "/guide/best-tshirts-streetwear-vigorbuy-spreadsheet" },
  { title: "Ultimate Jackets Guide", url: "/guide/ultimate-jackets-outerwear-guide" },
  { title: "Best Budget Finds Under $30", url: "/guide/best-budget-finds-under-30" },
  { title: "Top Accessories & Bags", url: "/guide/top-accessories-bags-vigorbuy" },
  { title: "Spreadsheet vs Direct Shopping", url: "/guide/vigorbuy-spreadsheet-vs-direct-shopping" },
  { title: "Quality Spotting Guide", url: "/guide/how-to-spot-quality-products" },
  { title: "Shipping & Delivery Guide", url: "/guide/shipping-delivery-guide-vigorbuy" },
  { title: "Safety & Buyer Protection", url: "/guide/safety-buyer-protection-vigorbuy" },
];

export default function Navbar() {
  const [catDropdownOpen, setCatDropdownOpen] = useState(false);
  const [guideDropdownOpen, setGuideDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [mobileGuideOpen, setMobileGuideOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const catRef = useRef<HTMLDivElement>(null);
  const guideRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    const handleClickOutside = (e: MouseEvent) => {
      if (catRef.current && !catRef.current.contains(e.target as Node)) {
        setCatDropdownOpen(false);
      }
      if (guideRef.current && !guideRef.current.contains(e.target as Node)) {
        setGuideDropdownOpen(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/70"
          : "bg-background-50/70 backdrop-blur-sm border-b border-background-200/30"
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap cursor-pointer">
            <span className="text-xl md:text-2xl font-heading font-bold text-foreground-950 tracking-tight">
              VigorBuy
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1 lg:gap-6">
            <Link to="/" className="px-3 py-2 text-sm font-semibold text-foreground-900 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer rounded-lg hover:bg-background-100">
              Home
            </Link>

            <div ref={catRef} className="relative">
              <button
                onClick={() => {
                  setCatDropdownOpen(!catDropdownOpen);
                  setGuideDropdownOpen(false);
                }}
                className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer rounded-lg hover:bg-background-100"
              >
                Categories
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${catDropdownOpen ? "rotate-180" : ""}`}></i>
              </button>

              {catDropdownOpen && (
                <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-60 bg-background-50 border border-background-200/80 rounded-xl overflow-hidden z-50 shadow-lg">
                  <div className="max-h-80 overflow-y-auto py-2">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer"
                        onClick={() => setCatDropdownOpen(false)}
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-accent-400 flex-shrink-0"></span>
                        {cat.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div ref={guideRef} className="relative">
              <button
                onClick={() => {
                  setGuideDropdownOpen(!guideDropdownOpen);
                  setCatDropdownOpen(false);
                }}
                className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer rounded-lg hover:bg-background-100"
              >
                Shopping Guides
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${guideDropdownOpen ? "rotate-180" : ""}`}></i>
              </button>

              {guideDropdownOpen && (
                <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-72 bg-background-50 border border-background-200/80 rounded-xl overflow-hidden z-50 shadow-lg">
                  <div className="max-h-96 overflow-y-auto py-2">
                    <p className="px-4 py-2 text-xs font-semibold text-foreground-400 uppercase tracking-wider">SEO Guides & Resources</p>
                    {guideArticles.map((article) => (
                      <Link
                        key={article.title}
                        to={article.url}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer"
                        onClick={() => setGuideDropdownOpen(false)}
                      >
                        <i className="ri-article-line text-accent-500 w-4 h-4 flex items-center justify-center flex-shrink-0"></i>
                        {article.title}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              vigorbuy sheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-700 cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70">
          <div className="px-4 py-4 max-h-[70vh] overflow-y-auto">
            <Link
              to="/"
              className="flex items-center gap-3 py-3 text-sm font-semibold text-foreground-900 cursor-pointer border-b border-background-100"
              onClick={() => setMobileMenuOpen(false)}
            >
              <i className="ri-home-4-line text-lg w-5 h-5 flex items-center justify-center text-accent-500"></i>
              Home
            </Link>

            <button
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
              className="w-full flex items-center justify-between py-3 text-sm font-semibold text-foreground-900 cursor-pointer border-b border-background-100"
            >
              <span className="flex items-center gap-3">
                <i className="ri-grid-line text-lg w-5 h-5 flex items-center justify-center text-accent-500"></i>
                Categories
              </span>
              <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${mobileCatOpen ? "rotate-180" : ""}`}></i>
            </button>
            {mobileCatOpen && (
              <div className="bg-background-100/50 rounded-lg mx-2 my-1 py-1">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-600 hover:text-foreground-950 transition-colors cursor-pointer"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-accent-400 flex-shrink-0"></span>
                    {cat.name}
                  </a>
                ))}
              </div>
            )}

            <button
              onClick={() => setMobileGuideOpen(!mobileGuideOpen)}
              className="w-full flex items-center justify-between py-3 text-sm font-semibold text-foreground-900 cursor-pointer border-b border-background-100"
            >
              <span className="flex items-center gap-3">
                <i className="ri-book-open-line text-lg w-5 h-5 flex items-center justify-center text-accent-500"></i>
                Shopping Guides
              </span>
              <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${mobileGuideOpen ? "rotate-180" : ""}`}></i>
            </button>
            {mobileGuideOpen && (
              <div className="bg-background-100/50 rounded-lg mx-2 my-1 py-1">
                {guideArticles.map((article) => (
                  <Link
                    key={article.title}
                    to={article.url}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-600 hover:text-foreground-950 transition-colors cursor-pointer"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <i className="ri-article-line text-accent-500 w-4 h-4 flex items-center justify-center flex-shrink-0"></i>
                    {article.title}
                  </Link>
                ))}
              </div>
            )}

            <div className="flex flex-col gap-2 mt-3 pt-3 border-t border-background-200/60">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full py-3 text-center text-sm font-semibold text-primary-600 bg-primary-50 rounded-xl cursor-pointer"
                onClick={() => setMobileMenuOpen(false)}
              >
                vigorbuy sheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full py-3 text-center text-sm font-semibold text-background-50 bg-primary-500 rounded-xl cursor-pointer"
                onClick={() => setMobileMenuOpen(false)}
              >
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}